import { type FormEvent, useEffect, useState } from "react";
import { Megaphone, Pencil, Plus, Trash2 } from "lucide-react";

import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  EmptyPanel,
  PageHero,
  PagePanel,
  PageShell,
  PanelHeading,
} from "@/components/interior-page";

type DisplayType = "banner" | "popup";

interface Announcement {
  id: number;
  message: string;
  title: string | null;
  display_type: DisplayType;
  is_active: boolean;
  start_at: string;
  end_at: string;
  created_at: string;
}

const toLocalDatetimeInput = (iso: string) => {
  // Convert ISO datetime to "YYYY-MM-DDTHH:mm" for datetime-local inputs
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const nowLocal = () => toLocalDatetimeInput(new Date().toISOString());

interface AnnouncementFormProps {
  initial?: Announcement;
  onSaved: () => void;
  onCancel: () => void;
}

function AnnouncementForm({ initial, onSaved, onCancel }: AnnouncementFormProps) {
  const toast = useToast();
  const [message, setMessage] = useState(initial?.message ?? "");
  const [title, setTitle] = useState(initial?.title ?? "");
  const [displayType, setDisplayType] = useState<DisplayType>(initial?.display_type ?? "banner");
  const [isActive, setIsActive] = useState(initial?.is_active ?? false);
  const [startAt, setStartAt] = useState(initial ? toLocalDatetimeInput(initial.start_at) : nowLocal());
  const [endAt, setEndAt] = useState(initial ? toLocalDatetimeInput(initial.end_at) : nowLocal());
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        message,
        title: displayType === "popup" ? title.trim() || null : null,
        display_type: displayType,
        is_active: isActive,
        start_at: new Date(startAt).toISOString(),
        end_at: new Date(endAt).toISOString(),
      };
      if (initial) {
        await api.patch(`/announcements/${initial.id}`, payload);
        toast.success("Announcement updated");
      } else {
        await api.post("/announcements", payload);
        toast.success("Announcement created");
      }
      onSaved();
    } catch {
      toast.error("Failed to save announcement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1.5">
        <Label>Display style</Label>
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {([
            { value: "banner", title: "Banner", desc: "Scrolling marquee on the home page." },
            { value: "popup", title: "Login popup", desc: "Dismissable dialog shown to users on login. Best for T&C updates." },
          ] as const).map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setDisplayType(opt.value)}
              className={cn(
                "rounded-xl border px-4 py-3 text-left transition-colors",
                displayType === opt.value
                  ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                  : "border-border/70 hover:border-primary/40",
              )}
            >
              <p className="text-sm font-semibold text-foreground">{opt.title}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{opt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {displayType === "popup" && (
        <div className="space-y-1.5">
          <Label htmlFor="ann-title">Title</Label>
          <Input
            id="ann-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. We've updated our Terms & Conditions"
          />
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="ann-message">Message</Label>
        <Textarea
          id="ann-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={displayType === "popup"
            ? "Full announcement text. Long messages collapse behind a “Show more” toggle…"
            : "Announcement text shown on home page…"}
          required
          rows={displayType === "popup" ? 6 : 3}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="ann-start">Start date &amp; time</Label>
          <Input
            id="ann-start"
            type="datetime-local"
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ann-end">End date &amp; time</Label>
          <Input
            id="ann-end"
            type="datetime-local"
            value={endAt}
            onChange={(e) => setEndAt(e.target.value)}
            required
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          id="ann-active"
          type="checkbox"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          className="h-4 w-4 rounded border-border accent-primary"
        />
        <Label htmlFor="ann-active" className="cursor-pointer">
          {displayType === "popup"
            ? "Active (show login popup when within date range)"
            : "Active (show on home page when within date range)"}
        </Label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? "Saving…" : initial ? "Save changes" : "Create"}
        </Button>
      </div>
    </form>
  );
}

export default function AdminAnnouncementsPage() {
  const toast = useToast();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get<Announcement[]>("/announcements");
      setAnnouncements(data);
    } catch {
      toast.error("Failed to load announcements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id: number) => {
    setDeleting(id);
    try {
      await api.delete(`/announcements/${id}`);
      toast.success("Announcement deleted");
      setAnnouncements((prev) => prev.filter((a) => a.id !== id));
    } catch {
      toast.error("Failed to delete announcement");
    } finally {
      setDeleting(null);
    }
  };

  const isCurrentlyActive = (a: Announcement) => {
    if (!a.is_active) return false;
    const now = new Date();
    return new Date(a.start_at) <= now && new Date(a.end_at) >= now;
  };

  return (
    <PageShell>
      <PageHero
        eyebrow="Admin"
        title="Announcements"
        description="Create home-page banners or login popups (e.g. to notify users about Terms & Conditions updates)."
        actions={
          !creating && !editing ? (
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              New Announcement
            </Button>
          ) : undefined
        }
      />

      {(creating || editing) && (
        <PagePanel>
          <PanelHeading
            title={editing ? "Edit Announcement" : "New Announcement"}
          />
          <div className="mt-4">
            <AnnouncementForm
              initial={editing ?? undefined}
              onSaved={() => {
                setCreating(false);
                setEditing(null);
                load();
              }}
              onCancel={() => {
                setCreating(false);
                setEditing(null);
              }}
            />
          </div>
        </PagePanel>
      )}

      <PagePanel>
        <PanelHeading title="All Announcements" />

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-7 w-7 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : announcements.length === 0 ? (
          <EmptyPanel
            icon={Megaphone}
            title="No announcements yet"
            description="Create an announcement to display a scrolling banner on the home page."
          />
        ) : (
          <ul className="mt-4 divide-y divide-border/70">
            {announcements.map((a) => {
              const live = isCurrentlyActive(a);
              return (
                <li key={a.id} className="flex items-start gap-4 py-4">
                  <div className="flex-1 min-w-0">
                    {a.display_type === "popup" && a.title && (
                      <p className="text-sm font-semibold text-foreground break-words">{a.title}</p>
                    )}
                    <p className="text-sm font-medium text-foreground break-words">{a.message}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {new Date(a.start_at).toLocaleString()} → {new Date(a.end_at).toLocaleString()}
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium capitalize text-slate-600">
                        {a.display_type === "popup" ? "Login popup" : "Banner"}
                      </span>
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium",
                          a.is_active
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border-slate-200 bg-slate-100 text-slate-500",
                        )}
                      >
                        {a.is_active ? "Active" : "Inactive"}
                      </span>
                      {live && (
                        <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                          Live now
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-1.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => { setCreating(false); setEditing(a); }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-rose-500 hover:bg-rose-50 hover:text-rose-600"
                      disabled={deleting === a.id}
                      onClick={() => handleDelete(a.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </PagePanel>
    </PageShell>
  );
}
